import { useState } from "react";
import { Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { trackingClient } from "@/lib/tracking/client";
import { getOrientedImage } from "@/lib/imageOrientation";

interface FileUploadProps {
  onFileSelect: (file: File | null, base64: string | null) => void;
  isAnalyzing: boolean;
  currentImage: string | null;
  label: string;
}

export function FileUpload({ onFileSelect, isAnalyzing, currentImage, label }: FileUploadProps) {
  const { t } = useI18n();
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = async (file: File | null) => {
    if (!file) {
      onFileSelect(null, null);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      trackingClient.track({
        event_name: 'photo_quality_failed',
        metadata: { 
          reason: 'Invalid file format',
          position: label 
        }
      });
      return;
    }

    try {
      // Get image with correct orientation
      const orientedBase64 = await getOrientedImage(file);
      onFileSelect(file, orientedBase64);
      
      // Track successful upload
      trackingClient.track({
        event_name: 'photo_uploaded',
        metadata: { 
          position: label,
          fileSize: file.size,
          fileType: file.type
        }
      });
    } catch (error) {
      console.error('Error processing image:', error);
      trackingClient.track({
        event_name: 'photo_quality_failed',
        metadata: { 
          reason: 'Image processing failed',
          position: label 
        }
      });
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileChange(files[0]);
    }
  };

  const handleClear = () => {
    onFileSelect(null, null);
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground block">{label}</label>
      
      {currentImage ? (
        <div className="relative">
          <img
            src={currentImage}
            alt={label}
            className="w-full h-48 object-contain rounded-xl border border-border bg-muted/20"
          />
          {!isAnalyzing && (
            <Button
              onClick={handleClear}
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 h-8 w-8 rounded-full"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ) : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
            transition-all duration-200
            ${isDragging ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50'}
            ${isAnalyzing ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
            disabled={isAnalyzing}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">
            {t('photo.instructions_hair')}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            JPG, PNG, or WEBP (max 10MB)
          </p>
        </div>
      )}
    </div>
  );
}
